import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import DlFacts from '../components/ui/DlFacts';
import Notice from '../components/ui/Notice';
import Button from '../components/ui/Button';
import { CriteriaSchedule, DecisionStops } from '../components/ui/PillarBlocks';
import { entity } from '../content/en';
import { organizationSchema, breadcrumbSchema } from '../components/ui/schema';

const About = () => {
  const { t, i18n } = useTranslation('cy');
  const meta = t('about.meta', { returnObjects: true });

  const schema = [
    organizationSchema(),
    breadcrumbSchema([{ label: t('nav.home'), to: '/' }, { label: t('nav.about'), to: '/about' }], i18n.language),
  ];

  const team = t('about.team.members', { returnObjects: true });

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/about" schema={schema} />

      <PageHero
        id="about-hero"
        image={`${process.env.PUBLIC_URL}/images/hero/hero-about.jpg`}
        eyebrow={t('about.hero.eyebrow')}
        title={t('about.hero.h1')}
        kicker={t('about.hero.kicker')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.about') }]} />}
      >
        <Notice variant="inline" className="cy-section-note">{t('about.hero.boundary')}</Notice>
        <p className="cy-section-action">
          <a className="cy-link" href="#about-entity">{t('about.hero.cta')}</a>
        </p>
      </PageHero>

      <section className="cy-section cy-section--alt cy-zone--doc" id="about-entity">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.entity.h2')} lead={t('about.entity.lead')} />
          <DlFacts
            rows={t('about.entity.fields', { returnObjects: true }).map((f) => ({
              label: f.label,
              value: f.value,
              copyable: ['ABN', 'ACN', 'Phone', 'Email', '电话', '邮箱'].includes(f.label),
            }))}
          />
          <p className="cy-fineprint cy-section-note">{t('about.entity.register')}</p>
          <Notice variant="inline" className="cy-section-note">{t('about.entity.boundary')}</Notice>
        </div>
      </section>

      <section className="cy-section" id="about-story">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.story.h2')} />
          <p className="cy-body">{t('about.story.body')}</p>
          <p className="cy-fineprint cy-section-note">{t('about.story.milestones')}</p>
        </div>
      </section>

      <section className="cy-section cy-section--alt" id="about-principles">
        <div className="cy-container cy-container--reading">
          <SectionHead eyebrow={t('about.principles.eyebrow')} title={t('about.principles.h2')} lead={t('about.principles.lead')} />
          <DecisionStops items={t('about.principles.items', { returnObjects: true })} />
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-risk">{t('about.principles.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section" id="about-team">
        <div className="cy-container">
          <SectionHead title={t('about.team.h2')} lead={t('about.team.lead')} />
          <ul className="cy-roster">
            {team.map((m) => (
              <li className="cy-roster__row" key={m.name}>
                <div>
                  <h3 className="cy-roster__name">{m.name}</h3>
                  <p className="cy-roster__role">{m.role}</p>
                </div>
                <div>
                  <p className="cy-roster__history">{m.history}</p>
                  <p className="cy-roster__verify">{m.verify}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="cy-fineprint cy-section-note">{t('about.team.fallback')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-capabilities">{t('about.team.capabilitiesLink')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="about-disclosure">
        <div className="cy-container">
          <SectionHead eyebrow={t('about.disclosure.eyebrow')} title={t('about.disclosure.h2')} lead={t('about.disclosure.lead')} />
          <CriteriaSchedule
            rows={t('about.disclosure.rows', { returnObjects: true })}
            term="practice"
            primary="detail"
          />
          <p className="cy-fineprint cy-section-note">{t('about.disclosure.note')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-capabilities">{t('about.disclosure.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section" id="about-relationships">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.relationships.h2')} />
          <p className="cy-lede cy-lede--flush">{t('about.relationships.contracting')}</p>
          <p className="cy-fineprint cy-section-note">{t('about.relationships.note')}</p>
        </div>
      </section>

      <section className="cy-section cy-section--cta" id="about-contact-cta">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.contactCta.h2')} />
          <DlFacts
            rows={[
              { label: t('ui.labels.phone'), value: entity.phone, href: entity.phoneHref },
              { label: t('ui.labels.email'), value: entity.email, href: entity.emailHref },
              { label: t('ui.labels.address'), value: entity.address },
            ]}
          />
          <div className="cy-section-action cy-section-action--row">
            <Button to="/contact?topic=partnership#contact-form">{t('about.contactCta.cta')}</Button>
            <Link className="cy-link" to="/submit-opportunity">{t('about.contactCta.submit')}</Link>
          </div>
          <p className="cy-fineprint cy-section-note">
            <Link className="cy-link" to="/privacy">{t('about.contactCta.privacy')}</Link>
            {' · '}
            <Link className="cy-link" to="/terms">{t('about.contactCta.terms')}</Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default About;
